import axios from 'axios'
import { getStoredPreviewAccessKey, PREVIEW_ACCESS_HEADER } from '../api/client'
import { saasApi } from './api'

const ACCESS_STORAGE_KEY = 'vistoera_mobile_access_v2'

export type MobileCaptureSessionStatus = 'pending' | 'paired' | 'expired' | 'revoked'

/** Which canonical processing flow a mobile-captured photo enters — decided
 * once at QR-creation time by which desktop entry point opened the modal
 * (物品图像处理 vs 平面图像处理). See `MobileImageUploadModal`'s `targetFlow`
 * prop — this is the one shared contract both entry points pass through
 * unchanged, never a second mobile-upload backend. */
export type MobileCaptureTargetFlow =
  | 'item_image_processing'
  | 'flat_image_processing'
  | 'workspace_asset_library'
  | 'video_asset_library'
  | 'audio_asset_library'

export type MobileAudioPresentationType =
  | 'MUSIC'
  | 'SFX'
  | 'AMBIENCE'
  | 'RECORDING'
  | 'REFERENCE_AUDIO'

/** Canonical video MIME whitelist — mirrors backend ``video_gen_video_allowed_mime``. */
export const MOBILE_CAPTURE_VIDEO_ACCEPT =
  'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm'

/** Canonical audio MIME whitelist — mirrors backend audio upload allowlist. */
export const MOBILE_CAPTURE_AUDIO_ACCEPT =
  'audio/*,.wav,.mp3,.m4a,.aac,.ogg,.flac,.webm,.opus'

export function isVideoCaptureFlow(flow?: MobileCaptureTargetFlow | null): boolean {
  return flow === 'video_asset_library'
}

export function isAudioCaptureFlow(flow?: MobileCaptureTargetFlow | null): boolean {
  return flow === 'audio_asset_library'
}

export interface MobileCaptureSessionOut {
  session_id: string
  status: MobileCaptureSessionStatus
  workspace_id: string
  target_flow?: MobileCaptureTargetFlow
  site_id?: string | null
  created_at?: string | null
  expires_at: string
  paired_at?: string | null
  consumed_at?: string | null
  last_activity_at?: string | null
  revoked_at?: string | null
  pairing_url?: string
  pairing_token?: string
  pairing_path?: string
  /** False when pairing_url is Desktop-only (127.0.0.1 / localhost). */
  mobile_reachable?: boolean
  pairing_base_source?: string
}

export interface MobileAuthPayload {
  auth_session_id: string
  workspace_id: string
  workspace_name: string
  source: string
  expires_at: string
  access_token: string
  access_expires_at: string
  target_flow?: MobileCaptureTargetFlow
}

export interface MobileWorkspaceOption {
  workspace_id: string
  workspace_name: string
  role: string
}

export interface MobileLoginResult {
  needs_workspace_selection: boolean
  workspaces: MobileWorkspaceOption[]
  auth_session_id?: string
  workspace_id?: string
  workspace_name?: string
  source?: string
  expires_at?: string
  access_token?: string
  access_expires_at?: string
}

export interface MobileCaptureMeOut {
  auth_session_id: string
  workspace_id: string
  workspace_name: string
  source: string
  expires_at: string
  last_activity_at?: string | null
  target_flow?: MobileCaptureTargetFlow
}

export function isLoopbackOrigin(url: string | undefined | null): boolean {
  if (!url) return true
  try {
    const host = new URL(url).hostname.toLowerCase()
    return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.startsWith('127.')
  } catch {
    return true
  }
}

/**
 * Mobile-reachable App Surface origin for QR pairing.
 * Never silently treat Desktop loopback as phone-reachable.
 *
 * Priority: VITE_MOBILE_CAPTURE_ORIGIN → non-loopback window.origin → null
 * (caller should prefer server pairing_url from PUBLIC_ADMIN_BASE_URL / LAN auto).
 */
export function resolveMobileCaptureOrigin(): string | null {
  const fromEnv = (import.meta.env.VITE_MOBILE_CAPTURE_ORIGIN as string | undefined)?.trim()
  if (fromEnv) {
    const cleaned = fromEnv.replace(/\/+$/, '')
    if (!isLoopbackOrigin(cleaned)) return cleaned
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin.replace(/\/+$/, '')
    if (!isLoopbackOrigin(origin)) return origin
  }
  return null
}

export function buildPairingUrl(pairingToken: string, originOverride?: string | null): string | null {
  const origin = (originOverride || resolveMobileCaptureOrigin() || '').replace(/\/+$/, '')
  if (!origin || isLoopbackOrigin(origin)) return null
  return `${origin}/mobile-capture/pair/${pairingToken}`
}

/** Canonical QR URL: prefer backend pairing_url when mobile-reachable. */
export function resolveSessionPairingUrl(data: MobileCaptureSessionOut): {
  url: string
  mobileReachable: boolean
  source: string
} {
  if (data.pairing_url && data.mobile_reachable === true) {
    return {
      url: data.pairing_url,
      mobileReachable: true,
      source: data.pairing_base_source || 'server',
    }
  }
  if (data.pairing_url && !isLoopbackOrigin(data.pairing_url)) {
    return {
      url: data.pairing_url,
      mobileReachable: true,
      source: data.pairing_base_source || 'server',
    }
  }
  if (data.pairing_token) {
    const rebuilt = buildPairingUrl(data.pairing_token)
    if (rebuilt) {
      return { url: rebuilt, mobileReachable: true, source: 'client_origin' }
    }
  }
  const diagnostic =
    data.pairing_url ||
    (data.pairing_token ? `${window.location.origin}/mobile-capture/pair/${data.pairing_token}` : '')
  return {
    url: diagnostic,
    mobileReachable: false,
    source: data.pairing_base_source || 'loopback_fallback',
  }
}

export const createMobileCaptureSession = (
  workspaceId: string,
  targetFlow: MobileCaptureTargetFlow = 'item_image_processing',
  siteId?: string | null,
  audioPresentationType?: MobileAudioPresentationType | null,
) =>
  saasApi.post<MobileCaptureSessionOut>(`/workspaces/${workspaceId}/mobile-capture/sessions`, {
    target_flow: targetFlow,
    ...(siteId ? { site_id: siteId } : {}),
    ...(audioPresentationType ? { audio_presentation_type: audioPresentationType } : {}),
  })

export const getMobileCaptureSession = (workspaceId: string, sessionId: string) =>
  saasApi.get<MobileCaptureSessionOut>(`/workspaces/${workspaceId}/mobile-capture/sessions/${sessionId}`)

export const revokeMobileCaptureSession = (workspaceId: string, sessionId: string) =>
  saasApi.post<MobileCaptureSessionOut>(`/workspaces/${workspaceId}/mobile-capture/sessions/${sessionId}/revoke`)

function mobileApiBase(): string {
  const raw = (import.meta.env.VITE_SAAS_API_BASE_URL as string | undefined)?.trim()
    || (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim()
  if (!raw) return '/api/saas'
  const trimmed = raw.replace(/\/+$/, '').replace(/\/api$/, '')
  return trimmed.endsWith('/api/saas') ? trimmed : `${trimmed}/api/saas`
}

const mobileSaasApi = axios.create({
  baseURL: mobileApiBase(),
  timeout: 120000,
  withCredentials: true,
})

mobileSaasApi.interceptors.request.use((config) => {
  const key = getStoredPreviewAccessKey()
  if (key) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>)[PREVIEW_ACCESS_HEADER] = key
  }
  return config
})

mobileSaasApi.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err.response?.data?.detail
    const message =
      detail && typeof detail === 'object'
        ? detail.message
        : typeof detail === 'string'
          ? detail
          : err.message
    const wrapped = new Error(typeof message === 'string' ? message : '请求失败') as Error & {
      status?: number
      code?: string
    }
    wrapped.status = err.response?.status
    wrapped.code = detail && typeof detail === 'object' ? detail.code : undefined
    return Promise.reject(wrapped)
  },
)

export function storeMobileAccess(accessToken: string | null) {
  if (!accessToken) {
    sessionStorage.removeItem(ACCESS_STORAGE_KEY)
    return
  }
  sessionStorage.setItem(ACCESS_STORAGE_KEY, accessToken)
}

export function readMobileAccess(): string | null {
  return sessionStorage.getItem(ACCESS_STORAGE_KEY)
}

export function clearMobileAccess() {
  sessionStorage.removeItem(ACCESS_STORAGE_KEY)
}

export async function pairMobileCapture(pairingToken: string): Promise<MobileAuthPayload> {
  const { data } = await mobileSaasApi.post<MobileAuthPayload>('/mobile-capture/pair', {
    pairing_token: pairingToken,
  })
  storeMobileAccess(data.access_token)
  return data
}

export async function mobilePasswordLogin(email: string, password: string): Promise<MobileLoginResult> {
  const { data } = await mobileSaasApi.post<MobileLoginResult>('/mobile-capture/auth/login', { email, password })
  if (data.access_token) storeMobileAccess(data.access_token)
  return data
}

export async function mobileSelectWorkspace(
  email: string,
  password: string,
  workspaceId: string,
): Promise<MobileAuthPayload> {
  const { data } = await mobileSaasApi.post<MobileAuthPayload>('/mobile-capture/auth/select-workspace', {
    email,
    password,
    workspace_id: workspaceId,
  })
  storeMobileAccess(data.access_token)
  return data
}

export async function refreshMobileAuth(): Promise<MobileAuthPayload> {
  const { data } = await mobileSaasApi.post<MobileAuthPayload>('/mobile-capture/auth/refresh')
  storeMobileAccess(data.access_token)
  return data
}

export async function logoutMobileAuth(accessToken: string): Promise<void> {
  try {
    await mobileSaasApi.post('/mobile-capture/auth/logout', null, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  } finally {
    clearMobileAccess()
  }
}

export async function fetchMobileCaptureMe(accessToken: string): Promise<MobileCaptureMeOut> {
  const { data } = await mobileSaasApi.get<MobileCaptureMeOut>('/mobile-capture/me', {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  return data
}

export class MobileCaptureUploadError extends Error {
  status?: number
  code?: string
  constructor(message: string, opts?: { status?: number; code?: string }) {
    super(message)
    this.name = 'MobileCaptureUploadError'
    this.status = opts?.status
    this.code = opts?.code
  }
}

export interface MobileUploadResult {
  id: string
  original_filename: string
  thumbnail_url?: string | null
  original_url?: string | null
}

/** Multipart mobile capture upload — images and videos share one endpoint. */
export type MobileCaptureUploadMethod = 'file' | 'recording'

export async function uploadMobileCaptureMedia(
  accessToken: string,
  file: File,
  opts?: {
    onProgress?: (pct: number) => void
    signal?: AbortSignal
    attemptId?: string
    captureMethod?: MobileCaptureUploadMethod
  },
): Promise<MobileUploadResult> {
  const form = new FormData()
  form.append('file', file, file.name || 'mobile-capture.jpg')
  form.append('capture_method', opts?.captureMethod || 'file')
  try {
    const { data } = await mobileSaasApi.post<MobileUploadResult>(
      '/mobile-capture/upload',
      form,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(opts?.attemptId ? { 'X-Upload-Attempt-Id': opts.attemptId } : {}),
        },
        signal: opts?.signal,
        onUploadProgress: (event) => {
          if (!opts?.onProgress || !event.total) return
          opts.onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)))
        },
      },
    )
    opts?.onProgress?.(100)
    return data
  } catch (err) {
    const ax = err as {
      response?: { status?: number; data?: { detail?: string | { code?: string; message?: string } } }
      code?: string
      message?: string
    }
    const status = ax.response?.status
    const detail = ax.response?.data?.detail
    const code = detail && typeof detail === 'object' ? detail.code : undefined
    let message: string
    if (detail && typeof detail === 'object') {
      message = detail.message || '上传失败'
    } else if (typeof detail === 'string' && detail.trim()) {
      message = detail
    } else if (status === undefined) {
      message =
        ax.code === 'ECONNABORTED' || /timeout/i.test(String(ax.message))
          ? '网络连接超时，请检查网络后重试'
          : '网络连接失败，请检查网络后重试'
    } else if (status >= 500) {
      message = '上传服务暂时不可用，请稍后重试'
    } else {
      message = ax.message || '上传失败'
    }
    throw new MobileCaptureUploadError(message, { status, code })
  }
}

/** @deprecated Use uploadMobileCaptureMedia */
export const uploadMobileCaptureImage = uploadMobileCaptureMedia

/** Transparent access refresh for the upload worker — never clears the queue. */
export async function ensureMobileAccessToken(
  current: string | null,
  onRotated?: (token: string) => void,
): Promise<string | null> {
  if (current) return current
  try {
    const refreshed = await refreshMobileAuth()
    onRotated?.(refreshed.access_token)
    return refreshed.access_token
  } catch {
    return null
  }
}
